import React from "react";
import styled from "styled-components";

import SystemNoticePill from "./SystemNoticePill";
import type { DmSystemContent } from "../../types/systemContent";
import {
  formatOfferDateTime,
  formatOfferAmountWithTax,
  formatOfferAcceptPositionInvite,
  formatOfferRoleScope,
} from "../../utils/offerFormatters";
import { formatYen } from "../../utils/money";
import { addConsumptionTax } from "../../utils/tax";

/** 値が無いときに表示するダッシュ記号（OfferSummaryCard と表現を揃える）。 */
const EMPTY_DASH = "—";

interface SystemMessageContentProps {
  systemContent: DmSystemContent | null | undefined;
  /** system_content が null や未知の kind だった時のフォールバック表示テキスト */
  fallbackText?: string;
}

// ===== スタイル =====

const SummaryCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border: 1px solid var(--soremo-blue);
  border-radius: 8px;
  color: var(--soremo-text);
  text-align: left;
`;

const SummaryHeader = styled.div.attrs({ className: "heading-spacing-13" })`
  color: var(--soremo-blue);
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 8px;
  align-items: baseline;
`;

const SummaryLabel = styled.div.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
`;

const SummaryValue = styled.div.attrs({ className: "bodytext-13" })``;

// ラベルを持たない一文の注記（招聘ポジション・再委託可否など）。OfferSummaryCard と表現を揃える。
const SummaryNote = styled.div.attrs({ className: "bodytext-13" })``;

// システムメッセージは控えめな pill バッジで表示する。
// 中央寄せは親（MessageItem の MessageSystem）が担う。
const PlainLine = SystemNoticePill;

// ===== 本体 =====

/**
 * DM の `type === "system"` メッセージ本文をレンダリングする。
 *
 * 仕様書 dm-messages.md §4 / dm-threads.md §5 の `system_content` Discriminated Union に従い、
 * `kind` ごとに表示バリアントを切り替える。
 *
 * - `offer_summary`        契約条件サマリ（スコープ / 納期 / 対価 / 成果物有無）をカード表示
 * - `offer_accepted`       「オファー成立」
 * - `inspection_completed` 「検収完了」
 * - `escrow_decreased`     減額分の予約解除
 * - `contract_amendment_*` / `contract_amended`
 *                          締結後の契約更新（提案 / 承認 / 却下 / 取り下げ）の差分カード
 * - `plain`                `text` をそのまま pill 表示
 * - 未知の kind / null      `fallbackText` を表示（既存挙動の互換）
 *
 * 注: `delivery_submitted`（テイクバッジへ移行）/ `escrow_locked` /
 * `escrow_released` / `escrow_unlocked` は DM 画面側でタイムライン非表示にしている。
 * 契約更新は金額以外の変更も含むため、`escrow_*` とは別に差分カードで見せる。
 */
const SystemMessageContent: React.FC<SystemMessageContentProps> = ({
  systemContent,
  fallbackText,
}) => {
  if (!systemContent) {
    return <PlainLine>{fallbackText ?? ""}</PlainLine>;
  }

  switch (systemContent.kind) {
    case "offer_summary": {
      const {
        assigned_roles,
        scope,
        deadline,
        contract_amount,
        accept_position,
        allow_subcontracting,
      } = systemContent.summary;
      // 送信時・確認時・DM 表示で同一表記にする（OfferSummaryCard と同じ項目・整形）。
      const acceptPositionInvite =
        formatOfferAcceptPositionInvite(accept_position);
      const roleScopeText = formatOfferRoleScope(assigned_roles, scope, EMPTY_DASH);
      return (
        <SummaryCard>
          <SummaryHeader>オファー詳細</SummaryHeader>
          <SummaryRow>
            <SummaryLabel>ロール</SummaryLabel>
            <SummaryValue>{roleScopeText}</SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>納期</SummaryLabel>
            <SummaryValue>
              {formatOfferDateTime(deadline, EMPTY_DASH)}
            </SummaryValue>
          </SummaryRow>
          <SummaryRow>
            <SummaryLabel>対価</SummaryLabel>
            <SummaryValue>
              {formatOfferAmountWithTax(contract_amount, EMPTY_DASH)}
            </SummaryValue>
          </SummaryRow>
          {acceptPositionInvite && (
            <SummaryNote>{acceptPositionInvite}</SummaryNote>
          )}
          {allow_subcontracting === true && (
            <SummaryNote>再委託が可能です</SummaryNote>
          )}
        </SummaryCard>
      );
    }
    case "offer_accepted":
      return <PlainLine>オファー成立</PlainLine>;
    case "delivery_submitted":
      return <PlainLine>成果物が提出されました</PlainLine>;
    case "inspection_completed":
      return <PlainLine>検収完了</PlainLine>;
    case "escrow_locked": {
      const amount = formatYen(
        addConsumptionTax(Number(systemContent.amount)),
        EMPTY_DASH,
      );
      if (systemContent.kind_detail === "amount_edit") {
        return <PlainLine>{`対価の増額分 ${amount} を支払い予約しました。`}</PlainLine>;
      }
      return <PlainLine>{`${amount} を支払い予約しました。`}</PlainLine>;
    }
    case "escrow_released": {
      const amount = formatYen(
        addConsumptionTax(Number(systemContent.amount)),
        EMPTY_DASH,
      );
      // 旧仕様（着手金 rate ベース）の互換表示。新規オファーでは発火せず、
      // 過去データを表示するためだけに残す（新仕様は full_on_accept）。
      if (systemContent.kind_detail === "advance") {
        return <PlainLine>{`着手金 ${amount} の支払いが実行されました。`}</PlainLine>;
      }
      if (systemContent.kind_detail === "full_on_accept") {
        return (
          <PlainLine>
            {`契約金額 ${amount} を予算として確保しました。`}
          </PlainLine>
        );
      }
      return <PlainLine>{`残金 ${amount} の支払いが実行されました。`}</PlainLine>;
    }
    case "escrow_decreased": {
      const amount = formatYen(
        addConsumptionTax(Number(systemContent.amount)),
        EMPTY_DASH,
      );
      return <PlainLine>{`対価の減額分 ${amount} の予約を解除しました。`}</PlainLine>;
    }
    case "escrow_unlocked": {
      const amount = formatYen(
        addConsumptionTax(Number(systemContent.amount)),
        EMPTY_DASH,
      );
      return <PlainLine>{`${amount} の支払い予約を解除しました。`}</PlainLine>;
    }
    // 契約更新まわりは結果だけを 1 行で残す。
    //
    // `contract_amended` にここでの分岐が無いのは意図的。DM 画面が契約アイテム
    // （差分 + 更新後の契約書 PDF）として専用描画するため、`MessageItem` までは
    // 届かない（`ContractAmendedTimelineItem`）。同じく承認待ちの
    // `contract_amendment_proposed` も差分パネルとして描き替えられるので、
    // ここに来るのは決着済みの提案だけ。
    case "contract_amendment_proposed":
      return <PlainLine>契約更新が提案されました</PlainLine>;
    case "contract_amendment_rejected":
      return <PlainLine>契約更新は却下されました</PlainLine>;
    case "contract_amendment_withdrawn":
      return (
        <PlainLine>
          {systemContent.reason === "status_changed"
            ? "契約更新の提案は失効しました"
            : "契約更新の提案は取り下げられました"}
        </PlainLine>
      );
    case "plain":
      return <PlainLine>{systemContent.text}</PlainLine>;
    default:
      return <PlainLine>{fallbackText ?? ""}</PlainLine>;
  }
};

export default SystemMessageContent;
